import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  alpha,
  useTheme
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  LocalOffer as DiscountIcon,
  Receipt as TaxIcon,
  CreditCard as CreditIcon
} from '@mui/icons-material';

const FinancialSummarySection = ({ data, formatCurrency }) => {
  // Format date without timezone issues
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + 'T12:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };
  const theme = useTheme();
  const { poCreation, lineItems } = data;

  // Calculate totals from line items if not in poCreation
  const calculateTotalsFromLineItems = () => {
    if (!lineItems || lineItems.length === 0) return null;
    
    const subtotal = lineItems.reduce((sum, item) => {
      return sum + (parseFloat(item.line_total || item.total_price || 0));
    }, 0);
    
    const discountPercent = parseFloat(poCreation?.discount_percent || 0);
    const discountAmount = subtotal * (discountPercent / 100);
    const afterDiscount = subtotal - discountAmount;
    
    const taxRate = parseFloat(poCreation?.tax_rate || 0);
    const taxAmount = afterDiscount * (taxRate / 100);
    
    const shippingFee = parseFloat(poCreation?.shipping_fee || 0);
    const handlingFee = parseFloat(poCreation?.handling_fee || 0);
    const otherFees = parseFloat(poCreation?.other_fees || 0);
    
    const totalBeforeCredit = afterDiscount + taxAmount + shippingFee + handlingFee + otherFees;
    const creditApplied = parseFloat(poCreation?.credit_applied || 0);
    const amountDue = totalBeforeCredit - creditApplied;
    
    return {
      subtotal,
      discountPercent,
      discountAmount,
      taxRate,
      taxAmount,
      shippingFee,
      handlingFee,
      otherFees,
      totalBeforeCredit,
      creditApplied,
      amountDue
    };
  };

  // Use poCreation totals if available, otherwise calculate from line items
  const calculatedTotals = calculateTotalsFromLineItems();
  const hasPOCreationTotals = poCreation && (poCreation.subtotal || poCreation.total_amount || poCreation.amount_due);
  const useCalculated = !hasPOCreationTotals && calculatedTotals;

  const subtotal = useCalculated && calculatedTotals 
    ? calculatedTotals.subtotal 
    : parseFloat(poCreation?.subtotal || 0);
  const discountPercent = parseFloat(poCreation?.discount_percent || 0);
  const discountAmount = useCalculated && calculatedTotals
    ? calculatedTotals.discountAmount
    : parseFloat(poCreation?.discount_amount || 0);
  const taxRate = parseFloat(poCreation?.tax_rate || 0);
  const taxAmount = useCalculated && calculatedTotals
    ? calculatedTotals.taxAmount
    : parseFloat(poCreation?.tax_amount || 0);
  const shippingFee = parseFloat(poCreation?.shipping_fee || 0);
  const handlingFee = parseFloat(poCreation?.handling_fee || 0);
  const otherFees = parseFloat(poCreation?.other_fees || 0);
  const totalBeforeCredit = useCalculated && calculatedTotals
    ? calculatedTotals.totalBeforeCredit
    : parseFloat(poCreation?.total_before_credit || poCreation?.total_amount || 0);
  const creditApplied = parseFloat(poCreation?.credit_applied || 0);
  const amountDue = useCalculated && calculatedTotals
    ? calculatedTotals.amountDue
    : parseFloat(poCreation?.amount_due || poCreation?.total_amount || 0);

  // Don't return null - show section even if poCreation is null (we can calculate from line items)
  if (!poCreation && !calculatedTotals) return null;

  return (
    <Card sx={{ 
      mb: 3,
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: '#f0f9f4',
      boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.1)}`,
      position: 'sticky',
      top: 20,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 20px ${alpha(theme.palette.success.main, 0.15)}`
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.success.main, 0.1)
          }}>
            <MoneyIcon sx={{ fontSize: 28, color: 'success.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Financial Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pricing breakdown
            </Typography>
          </Box>
        </Box>

        {/* Line Items Subtotal */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Subtotal
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {formatCurrency(subtotal)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Discount */}
        {discountAmount > 0 && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DiscountIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Discount ({discountPercent}%)
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={500} color="error.main">
                -{formatCurrency(discountAmount)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Tax */}
        {taxAmount > 0 && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TaxIcon sx={{ fontSize: 18, color: 'info.main' }} />
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Tax ({taxRate}%)
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={500} color="success.main">
                +{formatCurrency(taxAmount)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Shipping Fee */}
        {shippingFee > 0 && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Shipping Fee
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                +{formatCurrency(shippingFee)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Handling Fee */}
        {handlingFee > 0 && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Handling Fee
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                +{formatCurrency(handlingFee)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Other Fees */}
        {otherFees > 0 && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Other Fees
                {poCreation?.other_fees_description && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    ({poCreation.other_fees_description})
                  </Typography>
                )}
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                +{formatCurrency(otherFees)}
              </Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Total Before Credit */}
        <Box sx={{ 
          mb: 2,
          p: 2,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={500}>
              Total
            </Typography>
            <Typography variant="h6" fontWeight={500} color="primary.main">
              {formatCurrency(totalBeforeCredit)}
            </Typography>
          </Box>
        </Box>

        {/* Credit Applied */}
        {creditApplied > 0 && (
          <>
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditIcon sx={{ fontSize: 18, color: 'success.main' }} />
                  <Typography variant="body2" color="success.main" fontWeight={500}>
                    Credit Applied
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight={500} color="success.main">
                  -{formatCurrency(creditApplied)}
                </Typography>
              </Box>
              {poCreation?.credit_notes && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 3.5 }}>
                  {poCreation.credit_notes}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Amount Due */}
        <Box sx={{ 
          p: 2.5,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.success.main, 0.12),
          border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={500} color="success.main">
              AMOUNT DUE
            </Typography>
            <Typography variant="h5" fontWeight={500} color="success.main">
              {formatCurrency(amountDue)}
            </Typography>
          </Box>
        </Box>

        {/* Payment Terms */}
        {(poCreation?.payment_terms || poCreation?.payment_method) && (
          <Box sx={{ 
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`
          }}>
            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 1.5 }}>
              Payment Information
            </Typography>
            {poCreation?.payment_terms && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Terms
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {poCreation.payment_terms}
                </Typography>
              </Box>
            )}
            {poCreation?.payment_method && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Method
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {poCreation.payment_method}
                </Typography>
              </Box>
            )}
            {(poCreation?.early_payment_discount || 0) > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Early Payment
                </Typography>
                <Typography variant="body2" fontWeight={500} color="success.main">
                  {poCreation.early_payment_discount}% ({poCreation.early_payment_days || 10} days)
                </Typography>
              </Box>
            )}
            {(poCreation?.late_fee_rate || 0) > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Late Fee
                </Typography>
                <Typography variant="body2" fontWeight={500} color="error.main">
                  {poCreation.late_fee_rate}% per month
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Deposit Information */}
        {poCreation?.deposit_required && (
          <Box sx={{ 
            mt: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
          }}>
            <Typography variant="caption" color="warning.main" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 1 }}>
              Deposit Required
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatCurrency(poCreation.deposit_amount)} ({poCreation.deposit_percentage}%)
              </Typography>
            </Box>
            {poCreation?.deposit_paid && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Paid On
                </Typography>
                <Typography variant="body2" fontWeight={500} color="success.main">
                  {formatDate(poCreation.deposit_paid_date)}
                </Typography>
              </Box>
            )}
            {!poCreation?.deposit_paid && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                ⚠️ Deposit payment pending
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FinancialSummarySection;